// eslint-disable-next-line no-restricted-imports
import { Global, css } from "@emotion/react";
import { useMemo } from "react";

import { useSetting } from "metabase/common/hooks";
import { baseStyle, rootStyle } from "metabase/css/core/base.styled";
import { defaultFontFiles } from "metabase/css/core/fonts.styled";
import {
  isPublicEmbedding,
  isStaticEmbedding,
} from "metabase/embedding/config";
import { useSelector } from "metabase/redux";
import { getMetabaseCssVariables } from "metabase/styled-components/theme/css-variables";
import { useMantineTheme } from "metabase/ui";
import { getSitePath } from "metabase/utils/dom";
import { saveDomImageStyles } from "metabase/visualizations/lib/image-exports";

import { getFont, getFontFiles } from "../../selectors";

// DX-Comment: Importing lighten and alpha functions to create new css variables
import { alpha, lighten } from "metabase/ui/colors";

export const GlobalStyles = (): JSX.Element => {
  const font = useSelector(getFont);
  const fontFiles = useSelector(getFontFiles);
  const whitelabelColors = useSetting("application-colors");

  const sitePath = getSitePath();
  const theme = useMantineTheme();
  const { colorScheme } = theme.other;

  // DX-Comment: Extract brand color explicitly to track changes
  const brandColor = theme.colors?.brand?.[0];

  // This can get expensive so we should memoize it separately
  // DX-Comment: Using brandColor as explicit dependency to force re-generation
  const cssVariables = useMemo(() => {
    return getMetabaseCssVariables({ theme, whitelabelColors });
  }, [theme, whitelabelColors, brandColor]);

  const styles = useMemo(() => {
    return css`
      ${cssVariables}
      :root {
        --mb-default-font-family: "${font}";
        --mb-color-brand-lighten-28: ${lighten(brandColor, 0.28)}; /*DX-Comment Added new css variable */
        --mb-color-brand-alpha-15: ${alpha(brandColor, 0.15)}; /* DX-Comment Added new css variable */
        --mb-color-summarize: ${brandColor}; /* DX-Comment Added new css variable */
        --mb-color-summarize-8: ${alpha(brandColor, 0.8)}; /* DX-Comment Added new css variable */
      }

      ${defaultFontFiles({ baseUrl: sitePath })}
      ${fontFiles?.map(
        (file) => css`
          @font-face {
            font-family: "Custom";
            src: url(${encodeURI(file.src)}) format("${file.fontFormat}");
            font-weight: ${file.fontWeight};
            font-style: normal;
            font-display: swap;
          }
        `,
      )}
    ${saveDomImageStyles}
    body {
        font-size: 0.875em;
        ${isStaticEmbedding() || isPublicEmbedding()
          ? ""
          : `color-scheme: ${colorScheme};`}
        ${rootStyle}
      }

      ${baseStyle}
    `;
  }, [cssVariables, font, sitePath, fontFiles, colorScheme, brandColor]); // DX-Comment: Added brandColor as explicit dependency

  return <Global styles={styles} />;
};
